import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { TextField, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CancelIcon from '@mui/icons-material/Cancel';

import colorVariables from '../../styles/variables';

/** @type {Object<string, import('@mui/styles').CSSProperties>} */
const styles = {
  textFieldHasValue: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
        borderColor: colorVariables.purple900,
      },
      '& .MuiInputAdornment-root': {
        '& .MuiSvgIcon-root': {
          color: colorVariables.purple900,
        },
      },
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      width: '300px',
      height: '28px',
      fontSize: '13px',
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
          borderColor: colorVariables.purple900,
        },
        '& .MuiInputAdornment-root': {
          '& .MuiSvgIcon-root': {
            color: colorVariables.purple900,
          },
        },
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
          borderColor: colorVariables.purple900,
        },
        '& .MuiInputAdornment-root': {
          '& .MuiSvgIcon-root': {
            color: colorVariables.purple900,
          },
        },
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0px 0px',
      color: colorVariables.purple900,
      '&::placeholder': {
        fontSize: '14px',
        color: colorVariables.placeholderGrey,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colorVariables.grey400,
    },
    '& .MuiInputAdornment-root': {
      '& .MuiSvgIcon-root': {
        color: colorVariables.black,
        height: '20px',
      },
    },
    '& .MuiIconButton-root': {
      padding: '0 5px 0 0',
      color: colorVariables.purple900,
      '&:hover': {
        backgroundColor: 'transparent',
        '& .MuiSvgIcon-root': {
          color: colorVariables.purple700,
        },
      },
    },
  },
  textFieldV0: {
    width: '300px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 20,
    },

    '& .MuiOutlinedInput-input': {
      padding: '6px 0px',
    },

    '& .MuiOutlinedInput-input:-webkit-autofill': {
      WebkitTextFillColor: colorVariables.black,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colorVariables.tableGrey,
    },

    '&:hover': {
      '.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: colorVariables.black,
      },
      '.MuiOutlinedInput-root .Mui-disabled': {
        borderColor: colorVariables.textGrey,
      },
    },

    '& .Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
        borderColor: colorVariables.black,
      },
    },

    '& .MuiIconButton-root': {
      padding: '0 10px 0 0',
      color: colorVariables.black,

      '&:hover': {
        backgroundColor: 'transparent',

        '& .MuiSvgIcon-root': {
          color: colorVariables.grey400,
        },
      },
    },
  },
};

const useStyles = makeStyles(styles);

const TlAutoComplete = ({ value, onChange, onClear, placeholder, addClasses, unthemed, onFocus, isDisabled }) => {
  const classes = useStyles();
  const hasValue = () => value && value !== '';

  let className;
  if (unthemed) {
    className = classNames(classes.textFieldV0, ...addClasses);
  } else {
    const hasValueStyle = hasValue() ? [classes.textFieldHasValue] : [];
    className = classNames(classes.textField, ...hasValueStyle, ...addClasses);
  }

  return (
    <TextField
      disabled={isDisabled}
      onFocus={onFocus}
      id="autocomplete"
      value={value}
      placeholder={placeholder}
      className={className}
      onChange={onChange}
      variant="outlined"
      autoComplete="off"
      InputProps={{
        style: { fontSize: '13px' },
        startAdornment: (
          <IconButton
            onClick={() => {
              onClear();
            }}
            disableRipple
            disabled={value === ''}
            style={{ order: 1 }}
            size="large"
          >
            {hasValue() && <CancelIcon fontSize="small" />}
          </IconButton>
        ),
      }}
    />
  );
};

TlAutoComplete.defaultProps = {
  addClasses: [],
  value: null,
  unthemed: false,
  onFocus: () => {},
  isDisabled: false,
};

TlAutoComplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  addClasses: PropTypes.arrayOf(PropTypes.string),
  unthemed: PropTypes.bool,
  onFocus: PropTypes.func,
  isDisabled: PropTypes.bool,
};

export default TlAutoComplete;
